import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'umi';
import { Col, Row } from 'antd';
import ExtraFieldSelector from '@/components/ExtraFields/ExtraFieldSelector';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { selectExtraFieldTypesList } from '@/selectors/extraFieldsSelectors';

interface ExtraFieldsProps {
  relatedTo: 'customer' | 'result';
  onValuesChange: (values: any) => void;
}

const ExtraFields: React.FC<ExtraFieldsProps> = ({
  relatedTo,
  onValuesChange,
}) => {
  const allExtraFields = useSelector(selectExtraFieldTypesList);
  const [selectedExtraFields, setSelectedExtraFields] = useState<ExtraFieldTypeModel[]>([]);
  const [extraFieldValues, setExtraFieldValues] = useState<any>({});
  const initialized = useRef(false);

  const autoAttachedFields = useMemo(
    () => allExtraFields.filter((f) => f.related_to === relatedTo && f.is_auto_attached),
    [allExtraFields, relatedTo],
  );

  useEffect(() => {
    if (!initialized.current && autoAttachedFields.length > 0) {
      initialized.current = true;
      setSelectedExtraFields(autoAttachedFields);
    }
  }, [autoAttachedFields]);

  // Get selected field IDs
  const selectedFieldIds = selectedExtraFields.map(field => field.uuid);

  // Handle extra field selection
  const handleFieldSelect = (field: ExtraFieldTypeModel) => {
    setSelectedExtraFields([...selectedExtraFields, field]);
  };

  // Handle extra field removal
  const handleFieldRemove = (fieldUuid: string) => {
    setSelectedExtraFields(selectedExtraFields.filter(field => field.uuid !== fieldUuid));
    // Remove the field's value from state
    const newValues = { ...extraFieldValues };
    delete newValues[fieldUuid];
    setExtraFieldValues(newValues);
    onValuesChange(newValues);
  };

  // Handle extra field values change
  const handleExtraFieldValuesChange = (changedValues: any, allValues: any) => {
    setExtraFieldValues(allValues);
    onValuesChange(allValues);
  };

  return (
    <>
      {/* Extra Fields */}
      {selectedExtraFields.length > 0 && (
        <Row gutter={16}>
          <Col span={24}>
            <DynamicExtraFieldsForm
              fields={selectedExtraFields}
              keyField={'uuid'}
              onValuesChange={handleExtraFieldValuesChange}
              formProps={{ layout: 'vertical' }}
              removable
              onRemove={handleFieldRemove}
              grid
            />
          </Col>
        </Row>
      )}

      {/* Add Extra Field Selector */}
      <Row gutter={16}>
        <Col span={24}>
          <ExtraFieldSelector
            relatedTo={relatedTo}
            selectedFieldIds={selectedFieldIds}
            onFieldSelect={handleFieldSelect}
            onFieldCreated={handleFieldSelect}
          />
        </Col>
      </Row>
    </>
  );
};

export default ExtraFields;
